import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Navigation } from '@core/navigation/navigation.types';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { Observable, ReplaySubject, of, tap } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private _httpClient = inject(HttpClient);

    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) => {
                this._navigation.next(navigation);
            })
        );
    }

    getOfficeNavigation(): Observable<Navigation> {
        const defaultNavigation: FuseNavigationItem[] = [
            {
                id: 'reports',
                title: 'Reports',
                type: 'basic',
                icon: 'heroicons_outline:chart-pie',
                link: '/office/reports'
            },
            {
                id: 'quotes',
                title: 'Quotes',
                type: 'basic',
                icon: 'heroicons_outline:light-bulb',
                link: '/office/quotes'
            },
            {
                id: 'invoices',
                title: 'Invoices',
                type: 'basic',
                icon: 'heroicons_outline:currency-dollar',
                link: '/office/invoices'
            },
            {
                id: 'delivery-jobs',
                title: 'Jobs',
                type: 'basic',
                icon: 'heroicons_outline:truck',
                link: '/office/delivery-jobs'
            },
            {
                id: 'warehouse-jobs',
                title: 'Inventory',
                type: 'basic',
                icon: 'heroicons_outline:office-building',
                link: '/office/warehouse-jobs'
            },
            {
                id: 'users',
                title: 'Users',
                type: 'basic',
                icon: 'heroicons_outline:users',
                link: '/office/users'
            },
            {
                id: 'tenants',
                title: 'Tenants',
                type: 'basic',
                icon: 'heroicons_outline:ticket',
                link: '/office/tenants'
            },
            {
                id: 'developer',
                title: 'Developer',
                type: 'basic',
                icon: 'heroicons_outline:code',
                link: '/office/settings'
            },
            {
                id: 'chat',
                title: 'Chat',
                type: 'basic',
                icon: 'heroicons_outline:chat-alt-2',
                link: '/office/chat'
            },
            {
                id: 'task',
                title: 'Task',
                type: 'basic',
                icon: 'heroicons_outline:check-circle',
                link: '/office/task'
            },
            {
                id: 'notifications',
                title: 'Notifications',
                type: 'basic',
                icon: 'heroicons_outline:bell',
                link: '/office/notifications'
            },
            {
                id: 'qrReader',
                title: 'Scanner',
                type: 'basic',
                icon: 'heroicons_outline:qrcode',
                link: '/office/scanner'
            },
        ];
        const navigation: Navigation = {
            default: defaultNavigation,
            compact: defaultNavigation,
            futuristic: defaultNavigation,
            horizontal: defaultNavigation
        };
        return of(navigation).pipe(
            tap((navigation) => {
                this._navigation.next(navigation);
            })
        );;
    }

    getDriverNavigation(): Observable<Navigation> {
        const defaultNavigation: FuseNavigationItem[] = [
            {
                id: 'dashboard',
                title: 'Dashboard',
                type: 'basic',
                icon: 'heroicons_outline:home',
                link: '/driver/dashboard'
            },
            {
                id: 'reports',
                title: 'Reports',
                type: 'basic',
                icon: 'heroicons_outline:chart-pie',
                link: '/driver/reports'
            },
            {
                id: 'jobs',
                title: 'Jobs',
                type: 'basic',
                icon: 'heroicons_outline:truck',
                link: '/driver/jobs/pending'
            },
            //TODO Enable after setting up schedule
            /*{
                id: 'schedule',
                title: 'Schedule',
                type: 'basic',
                icon: 'heroicons_outline:map',
                link: '/driver/schedule'
            },*/
            {
                id: 'settings',
                title: 'Settings',
                type: 'basic',
                icon: 'heroicons_outline:cog',
                link: '/driver/settings'
            },
            {
                id: 'chat',
                title: 'Chat',
                type: 'basic',
                icon: 'heroicons_outline:chat-alt-2',
                link: '/driver/chat'
            }
        ];
        const navigation: Navigation = {
            default: defaultNavigation,
            compact: defaultNavigation,
            futuristic: defaultNavigation,
            horizontal: defaultNavigation
        };
        return of(navigation).pipe(
            tap((navigation) => {
                this._navigation.next(navigation);
            })
        );;
    }
}
